/**
 * Layer 4 — political borders, front lines and the optional hex grid.
 *
 *  - Borders run exactly along hex edges. Each side gets its own thin line in its darkened
 *    faction colour, offset a pixel or two into its own territory (a double rule, like printed
 *    political maps). Edges are oriented with the owning hex on the right so the offset is
 *    always inward, then chained into polylines.
 *  - Fronts (edges between hostile teams) are a thick red-black line, lightly smoothed, with
 *    triangular teeth pointing into enemy territory (from the viewer's point of view). Teeth are
 *    re-spaced whenever the zoom crosses a half-octave so they stay evenly spaced on screen.
 *  - Grid: faint 1 px hex outlines over land, fading in with zoom (settings.showGrid).
 */
import { EDGE_CORNERS, type FactionId, type HexGrid, NO_FACTION } from '@friction/engine';
import { Container, type Texture } from 'pixi.js';
import type { MarkerGlyphs } from '../atlas/glyphs';
import { chaikin, chainSegments, pointAt, polyLength } from '../geom/paths';
import { LineBatch } from '../gfx/lines';
import { QuadBatch, Scale } from '../gfx/quads';
import { darken, PAL } from '../palette';

const key = (x: number, y: number): number => Math.round(x * 4) * 131072 + Math.round(y * 4);

/** Chain oriented segments (end of one = start of the next). */
function chainOriented(segs: number[]): number[][] {
  const count = segs.length >> 2;
  const byStart = new Map<number, number[]>();
  const hasIncoming = new Set<number>();
  for (let s = 0; s < count; s++) {
    const k = key(segs[s * 4], segs[s * 4 + 1]);
    const l = byStart.get(k);
    if (l) l.push(s);
    else byStart.set(k, [s]);
    hasIncoming.add(key(segs[s * 4 + 2], segs[s * 4 + 3]));
  }
  const used = new Uint8Array(count);
  const out: number[][] = [];
  const run = (s0: number) => {
    const pts = [segs[s0 * 4], segs[s0 * 4 + 1]];
    let s = s0;
    for (;;) {
      used[s] = 1;
      pts.push(segs[s * 4 + 2], segs[s * 4 + 3]);
      const next = byStart.get(key(segs[s * 4 + 2], segs[s * 4 + 3]))?.find((n) => !used[n]);
      if (next === undefined) break;
      s = next;
    }
    out.push(pts);
  };
  // open chains first (start nodes without incoming edges), then closed loops
  for (let s = 0; s < count; s++) if (!used[s] && !hasIncoming.has(key(segs[s * 4], segs[s * 4 + 1]))) run(s);
  for (let s = 0; s < count; s++) if (!used[s]) run(s);
  return out;
}

export interface FactionInfo {
  color: number;
  team: number;
}

export class BordersLayer {
  readonly container = new Container({ label: 'borders' });
  private readonly grid: HexGrid;
  private readonly land: Uint8Array;
  private readonly borders = new LineBatch('borders', 8192);
  private readonly fronts = new LineBatch('fronts', 4096);
  private readonly gridLines = new LineBatch('hexgrid', 4096);
  private readonly teeth: QuadBatch;
  private readonly glyphs: MarkerGlyphs;
  private factions = new Map<FactionId, FactionInfo>();
  private viewer: FactionId | null = null;
  /** Viewer-oriented front polylines (viewer side on the right), for teeth placement. */
  private frontPaths: number[][] = [];
  private teethLevel = Number.NaN;
  private gridBuilt = false;

  constructor(grid: HexGrid, isLand: (h: number) => boolean, glyphs: MarkerGlyphs, atlasTexture: Texture) {
    this.grid = grid;
    this.glyphs = glyphs;
    this.land = new Uint8Array(grid.count);
    for (let h = 0; h < grid.count; h++) this.land[h] = isLand(h) ? 1 : 0;
    this.teeth = new QuadBatch('front-teeth', atlasTexture, 1024);
    this.container.addChild(this.gridLines.mesh, this.borders.mesh, this.fronts.mesh, this.teeth.mesh);
  }

  setFactions(factions: Map<FactionId, FactionInfo>, viewer: FactionId | null): void {
    this.factions = factions;
    this.viewer = viewer;
  }

  private hostile(a: FactionId, b: FactionId): boolean {
    if (a === NO_FACTION || b === NO_FACTION) return false;
    const fa = this.factions.get(a);
    const fb = this.factions.get(b);
    return !!fa && !!fb && fa.team !== fb.team;
  }

  private viewerSide(f: FactionId): boolean {
    if (this.viewer === null) return false;
    const v = this.factions.get(this.viewer);
    const x = this.factions.get(f);
    return f === this.viewer || (!!v && !!x && v.team === x.team);
  }

  /** Rebuild borders and fronts for a new ownership array. */
  rebuild(owner: Uint8Array, showFronts: boolean): void {
    const g = this.grid;
    const perFaction = new Map<FactionId, number[]>();
    const viewerFront: number[] = [];
    const otherFront: number[] = [];
    const push = (arr: number[], ax: number, ay: number, bx: number, by: number) => arr.push(ax, ay, bx, by);
    const segsOf = (f: FactionId): number[] => {
      const l = perFaction.get(f);
      if (l) return l;
      const n: number[] = [];
      perFaction.set(f, n);
      return n;
    };
    for (let h = 0; h < g.count; h++) {
      if (!this.land[h]) continue;
      const a = owner[h];
      for (let d = 0; d < 6; d++) {
        const n = g.neighbour(h, d);
        if (n < 0 || n < h || !this.land[n]) continue;
        const b = owner[n];
        if (a === b) continue;
        const [ca, cb] = EDGE_CORNERS[d];
        const p = g.corner(h, ca);
        const q = g.corner(h, cb);
        // p → q runs clockwise around h: h is on the right, n on the left
        if (showFronts && this.hostile(a, b)) {
          if (this.viewerSide(a)) push(viewerFront, p.x, p.y, q.x, q.y);
          else if (this.viewerSide(b)) push(viewerFront, q.x, q.y, p.x, p.y);
          else push(otherFront, p.x, p.y, q.x, q.y);
          continue;
        }
        // each side gets the edge oriented with its own hex on the right
        if (a !== NO_FACTION) push(segsOf(a), p.x, p.y, q.x, q.y);
        if (b !== NO_FACTION) push(segsOf(b), q.x, q.y, p.x, p.y);
      }
    }

    const bl = this.borders;
    bl.clear();
    for (const [f, segs] of perFaction) {
      const color = darken(this.factions.get(f)?.color ?? PAL.ink, 0.3);
      for (const raw of chainOriented(segs)) {
        // one light corner-cutting pass: still visibly on the hex edges, without the harsh zigzag
        const closed = isClosed(raw);
        const pts = chaikin(closed ? raw.slice(0, -2) : raw, 1, closed).pts;
        bl.add(pts, { width: 1.5, color, alpha: 0.9, minPx: 1.1, maxPx: 2.2, offsetPx: 1.3 }, { closed });
      }
    }
    bl.commit();

    const fl = this.fronts;
    fl.clear();
    this.frontPaths = [];
    for (const raw of chainOriented(viewerFront)) {
      const closed = isClosed(raw);
      const pts = chaikin(closed ? raw.slice(0, -2) : raw, 2, closed).pts;
      if (closed) pts.push(pts[0], pts[1]);
      this.frontPaths.push(pts);
      fl.add(pts, { width: 4.6, color: 0x2a0c09, alpha: 0.92, minPx: 3.4, maxPx: 6 }, { closed });
      fl.add(pts, { width: 2.2, color: 0xa3261c, alpha: 1, minPx: 1.6, maxPx: 3 }, { closed });
    }
    for (const c of chainSegments(otherFront)) {
      const pts = chaikin(c.closed ? c.pts.slice(0, -2) : c.pts, 2, c.closed).pts;
      fl.add(pts, { width: 3, color: 0x3a1410, alpha: 0.75, minPx: 2, maxPx: 4 }, { closed: c.closed });
    }
    fl.commit();
    this.teethLevel = Number.NaN;
  }

  private buildTeeth(zoomCss: number): void {
    const t = this.teeth;
    t.clear();
    const spacing = 15 / zoomCss;
    for (const pts of this.frontPaths) {
      const len = polyLength(pts);
      const n = Math.floor(len / spacing);
      if (n < 1) continue;
      const start = (len - (n - 1) * spacing) / 2;
      for (let i = 0; i < n; i++) {
        const [x, y, tx, ty] = pointAt(pts, start + i * spacing);
        // the viewer holds the right-hand side; teeth point left, into enemy ground
        t.push(
          x,
          y,
          0,
          -5.2,
          6.4,
          5.6,
          this.glyphs.tooth.frame,
          0xa3261c,
          1,
          Scale.Marker,
          Math.atan2(ty, tx),
        );
      }
    }
    t.commit();
  }

  private buildGrid(): void {
    const g = this.grid;
    const segs: number[] = [];
    for (let h = 0; h < g.count; h++) {
      for (let d = 0; d < 3; d++) {
        const n = g.neighbour(h, d);
        if (!this.land[h] && (n < 0 || !this.land[n])) continue;
        const [ca, cb] = EDGE_CORNERS[d];
        const p = g.corner(h, ca);
        const q = g.corner(h, cb);
        segs.push(p.x, p.y, q.x, q.y);
      }
      // bottom-left edges of the last row / left edges of the first column close the grid
      for (const d of [3, 4, 5]) {
        if (g.neighbour(h, d) >= 0 || !this.land[h]) continue;
        const [ca, cb] = EDGE_CORNERS[d];
        const p = g.corner(h, ca);
        const q = g.corner(h, cb);
        segs.push(p.x, p.y, q.x, q.y);
      }
    }
    for (const c of chainSegments(segs)) {
      this.gridLines.add(
        c.pts,
        { width: 0.7, color: PAL.ink, alpha: 0.2, minPx: 0.7, maxPx: 1.2 },
        { closed: c.closed },
      );
    }
    this.gridLines.commit();
    this.gridBuilt = true;
  }

  /** Per-frame: zoom uniforms, teeth re-spacing, grid fade. `markerUnit` = world per layout unit. */
  update(zoomCss: number, resolution: number, markerUnit: number, showGrid: boolean): void {
    this.borders.setZoom(zoomCss, resolution);
    this.fronts.setZoom(zoomCss, resolution);
    this.gridLines.setZoom(zoomCss, resolution);
    this.teeth.setUnits(markerUnit, markerUnit);
    const level = Math.round(Math.log2(zoomCss) * 2);
    if (level !== this.teethLevel) {
      this.teethLevel = level;
      this.buildTeeth(2 ** (level / 2));
    }
    const gridAlpha = showGrid ? Math.min(1, Math.max(0, (zoomCss - 0.55) / 0.35)) : 0;
    if (gridAlpha > 0 && !this.gridBuilt) this.buildGrid();
    this.gridLines.mesh.visible = gridAlpha > 0 && !this.gridLines.isEmpty;
    this.gridLines.mesh.alpha = gridAlpha;
  }

  destroy(): void {
    this.borders.destroy();
    this.fronts.destroy();
    this.gridLines.destroy();
    this.teeth.destroy();
    this.container.destroy();
  }
}

const isClosed = (pts: number[]): boolean =>
  pts.length > 6 &&
  Math.abs(pts[0] - pts[pts.length - 2]) < 0.01 &&
  Math.abs(pts[1] - pts[pts.length - 1]) < 0.01;
